import { __esDecorate, __runInitializers } from "tslib";
import { StringTag } from '#library/decorator.library.js';
import { isCallable, isDefined, isFunction, isNumber, isObject, isPromise, isString, isAbortSignal } from '#library/assertion.library.js';
import { Pledge } from './pledge.class.js';
import { Finalizer } from './finalizer.class.js';
import { onAbort, NOOP } from './aborter.library.js';
/**
 * Disposable subscription handle returned by event subscriptions (`stream.on(...)`).
 * Supports explicit resource management via `using sub = stream.on(...)`.
 */
let ReactiveSubscription = (() => {
    let _classDecorators = [StringTag('Reactive.Subscription')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    var ReactiveSubscription = class {
        static { _classThis = this; }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            ReactiveSubscription = _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
            __runInitializers(_classThis, _classExtraInitializers);
        }
        #closed = false;
        #cleanup = NOOP;
        #unbind = NOOP;
        constructor(cleanup, signal) {
            if (isCallable(cleanup))
                this.#cleanup = cleanup;
            this.#unbind = onAbort(signal, () => this.unsubscribe());
        }
        /** Whether the subscription has been detached */
        get closed() {
            return this.#closed;
        }
        /** Detaches the listener from the source stream */
        unsubscribe() {
            if (this.#closed)
                return;
            this.#closed = true;
            this.#unbind();
            this.#cleanup();
        }
        /** TC39 explicit resource management hook (calls unsubscribe) */
        [Symbol.dispose]() {
            this.unsubscribe();
        }
    };
    return ReactiveSubscription = _classThis;
})();
function cleanupReactiveState(state) {
    if (state.completed)
        return;
    state.completed = true;
    if (state.waiters.length > 0) {
        const queue = state.waiters.splice(0);
        for (const waiter of queue) {
            if (waiter.isPending)
                waiter.resolve({ done: true, value: undefined });
        }
    }
    if (state.endListeners.size > 0) {
        const listeners = [...state.endListeners];
        for (const listener of listeners) {
            try {
                listener();
            }
            catch (err) {
                console.error('Reactive end listener failed:', err);
            }
        }
    }
    state.dataListeners.clear();
    state.errorListeners.clear();
    state.endListeners.clear();
}
/**
 * ## Reactive
 *
 * Universal, zero-dependency async reactive stream combining push-based event emission
 * and pull-based `AsyncIterable` iteration with native TC39 resource management
 * (`using` / `await using`) and Garbage Collection finalization safety.
 *
 * @example
 * ```ts
 * const stream = new Reactive<number>();
 *
 * // Consumer 1: Pull-mode
 * queueMicrotask(async () => {
 *   const val = await stream.pull(); // 42
 * });
 *
 * // Producer: Push-mode
 * stream.push(42);
 * ```
 */
let Reactive = (() => {
    let _classDecorators = [StringTag('Reactive')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    var Reactive = class {
        static { _classThis = this; }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            Reactive = _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        }
        static Subscription = ReactiveSubscription;
        #tag;
        #bufferSize;
        #catch;
        #active = true;
        #completed = false;
        #emitted = 0;
        #buffer = [];
        #waiters = [];
        #dataListeners = new Set();
        #errorListeners = new Set();
        #endListeners = new Set();
        #stopCallback;
        #cleanupState;
        #unregisterFinalizer;
        constructor(arg) {
            const opts = isObject(arg) ? arg : isString(arg) ? { tag: arg } : {};
            this.#tag = opts.tag;
            this.#bufferSize = isNumber(opts.bufferSize) && opts.bufferSize >= 0 ? opts.bufferSize : Infinity;
            this.#catch = Boolean(opts.catch);
            this.#stopCallback = () => this.complete();
            const cleanupState = {
                completed: false,
                waiters: this.#waiters,
                endListeners: this.#endListeners,
                dataListeners: this.#dataListeners,
                errorListeners: this.#errorListeners,
            };
            this.#cleanupState = cleanupState;
            if (opts.finalizer !== false) {
                this.#unregisterFinalizer = Finalizer.register(this, () => {
                    cleanupReactiveState(cleanupState);
                });
            }
        }
        /** Identifies this object as a Reactive instance */
        get isReactive() {
            return true;
        }
        /** Returns the operational state of this stream */
        get state() {
            return {
                tag: this.#tag,
                active: this.#active,
                completed: this.#completed,
                emitted: this.#emitted,
                subscribers: this.#dataListeners.size + this.#errorListeners.size + this.#endListeners.size,
                queued: this.#waiters.length,
                buffered: this.#buffer.length,
            };
        }
        push(arg) {
            if (isFunction(arg)) {
                return this.on('data', arg);
            }
            if (!this.#active || this.#completed)
                return false;
            this.#emitted++;
            // 1. Dispatch to pending pull waiter if any
            let dispatchedToWaiter = false;
            while (this.#waiters.length > 0) {
                const waiter = this.#waiters.shift();
                if (waiter.isPending) {
                    waiter.resolve({ done: false, value: arg });
                    dispatchedToWaiter = true;
                    break;
                }
            }
            // 2. Buffer for future pull consumers if buffer space is available and not consumed by push listeners
            if (!dispatchedToWaiter && this.#dataListeners.size === 0 && this.#buffer.length < this.#bufferSize)
                this.#buffer.push(arg);
            // 3. Dispatch to all push listeners
            if (this.#dataListeners.size > 0) {
                const listeners = [...this.#dataListeners];
                for (const listener of listeners) {
                    try {
                        listener(arg, this.#stopCallback);
                    }
                    catch (err) {
                        this.error(err instanceof Error ? err : new Error(String(err)));
                    }
                }
            }
            return true;
        }
        /**
         * Casts a value to ALL pending pull waiters and push listeners simultaneously (multicast fan-out).
         *
         * @param value - The value to cast across all consumers
         * @returns True if the value was successfully emitted; false if completed or inactive
         */
        cast(value) {
            if (!this.#active || this.#completed)
                return false;
            this.#emitted++;
            // 1. Dispatch to all pending pull waiters
            if (this.#waiters.length > 0) {
                const waiters = this.#waiters.splice(0);
                for (const waiter of waiters) {
                    if (waiter.isPending)
                        waiter.resolve({ done: false, value });
                }
            }
            // 2. Dispatch to all push listeners
            if (this.#dataListeners.size > 0) {
                const listeners = [...this.#dataListeners];
                for (const listener of listeners) {
                    try {
                        listener(value, this.#stopCallback);
                    }
                    catch (err) {
                        this.error(err instanceof Error ? err : new Error(String(err)));
                    }
                }
            }
            return true;
        }
        /**
         * Broadcasts a value to ALL pending pull waiters and push listeners simultaneously.
         *
         * @alias cast
         * @param value - The value to broadcast
         * @returns True if the value was successfully emitted; false if completed or inactive
         */
        broadcast(value) {
            return this.cast(value);
        }
        /**
         * Emits a value to active pull-mode consumers or push-mode listeners.
         *
         * @alias push
         * @param value - The value to emit
         * @returns True if the value was successfully emitted; false if completed or inactive
         */
        emit(value) {
            return this.push(value);
        }
        #error;
        /**
         * Emits an error to registered error listeners or rejects pending pull consumers.
         *
         * @param err - The error instance to dispatch
         */
        error(err) {
            if (this.#completed)
                return;
            this.#error = err;
            if (this.#errorListeners.size > 0) {
                const listeners = [...this.#errorListeners];
                for (const listener of listeners) {
                    try {
                        listener(err, this.#stopCallback);
                    }
                    catch (e) {
                        console.error('Reactive error listener failed:', e);
                    }
                }
            }
            const hadWaiters = this.#waiters.length > 0;
            if (hadWaiters) {
                const queue = this.#waiters.splice(0);
                for (const waiter of queue) {
                    if (waiter.isPending) {
                        if (this.#catch) {
                            waiter.resolve({ done: true, value: undefined });
                        }
                        else {
                            waiter.reject(err);
                        }
                    }
                }
            }
            if (this.#catch) {
                this.complete();
            }
            else if (this.#errorListeners.size === 0 && !hadWaiters) {
                console.error('Unhandled Reactive error:', err);
            }
        }
        /**
         * Completes and closes the reactive stream.
         *
         * @param terminalValue - Optional final value to emit prior to closing
         */
        complete(terminalValue) {
            if (this.#completed)
                return;
            if (terminalValue !== undefined) {
                this.push(terminalValue);
            }
            this.#completed = true;
            this.#active = false;
            cleanupReactiveState(this.#cleanupState);
            if (this.#unregisterFinalizer) {
                this.#unregisterFinalizer();
                this.#unregisterFinalizer = undefined;
            }
        }
        /**
         * Alias for `complete()`.
         *
         * @alias complete
         * @param terminalValue - Optional final value to emit prior to closing
         */
        stop(terminalValue) {
            this.complete(terminalValue);
        }
        on(event, listener, options) {
            if (!isFunction(listener))
                return new Reactive.Subscription(() => { });
            if (options?.signal?.aborted)
                return new Reactive.Subscription(() => { }, options.signal);
            let actualListener = listener;
            if (options?.once) {
                actualListener = (...args) => {
                    sub.unsubscribe();
                    listener(...args);
                };
            }
            let cleanup;
            if (event === 'data') {
                this.#dataListeners.add(actualListener);
                cleanup = () => {
                    this.#dataListeners.delete(actualListener);
                };
                // Flush any existing buffered items to the new push listener
                if (this.#buffer.length > 0) {
                    const buffered = [...this.#buffer];
                    this.#buffer = [];
                    for (const item of buffered)
                        actualListener(item, this.#stopCallback);
                }
            }
            else if (event === 'error') {
                this.#errorListeners.add(actualListener);
                cleanup = () => {
                    this.#errorListeners.delete(actualListener);
                };
            }
            else if (event === 'end' || event === 'stop') {
                if (this.#completed) {
                    queueMicrotask(() => actualListener());
                    cleanup = () => { };
                }
                else {
                    this.#endListeners.add(actualListener);
                    cleanup = () => {
                        this.#endListeners.delete(actualListener);
                    };
                }
            }
            else {
                cleanup = () => { };
            }
            const sub = new Reactive.Subscription(cleanup, options?.signal);
            return sub;
        }
        once(event, listener, options) {
            return this.on(event, listener, { ...options, once: true });
        }
        /**
         * Unsubscribes a previously registered event listener.
         *
         * @param event - Event name
         * @param listener - Listener function to remove
         * @returns `this` for chaining
         */
        off(event, listener) {
            if (event === 'data')
                this.#dataListeners.delete(listener);
            else if (event === 'error')
                this.#errorListeners.delete(listener);
            else if (event === 'end' || event === 'stop')
                this.#endListeners.delete(listener);
            return this;
        }
        // ── Lifecycle & Cancellation ───────────────────────────────────────────────
        /**
         * Returns a new Reactive stream that mirrors the source but completes automatically
         * when the given notifier (AbortSignal, Promise, or trigger Reactive stream) fires.
         *
         * @param notifier - An AbortSignal, Promise, or Reactive stream bounding this stream's lifecycle.
         * @returns A new Reactive stream
         */
        until(notifier) {
            const output = new Reactive({
                tag: this.#tag ? `${this.#tag}.until` : 'Reactive.until',
                bufferSize: this.#bufferSize,
                catch: this.#catch,
            });
            let notifierCleanups = [];
            const stop = () => {
                subData.unsubscribe();
                subError.unsubscribe();
                subEnd.unsubscribe();
                for (const cleanup of notifierCleanups)
                    cleanup();
                notifierCleanups = [];
                output.complete();
            };
            const subData = this.on('data', (val) => output.push(val));
            const subError = this.on('error', (err) => output.error(err));
            const subEnd = this.on('end', () => output.complete());
            output.on('end', () => {
                subData.unsubscribe();
                subError.unsubscribe();
                subEnd.unsubscribe();
                for (const cleanup of notifierCleanups)
                    cleanup();
                notifierCleanups = [];
            });
            if (isAbortSignal(notifier)) {
                notifierCleanups.push(onAbort(notifier, stop));
            }
            else if (isPromise(notifier) || (isDefined(notifier) && isFunction(notifier.then))) {
                notifier.then(stop, stop);
            }
            else if (isDefined(notifier) && isFunction(notifier.on)) {
                const trigger = notifier;
                const subTriggerData = trigger.on('data', stop, { once: true });
                const subTriggerEnd = trigger.on('end', stop, { once: true });
                notifierCleanups.push(() => {
                    subTriggerData.unsubscribe();
                    subTriggerEnd.unsubscribe();
                });
            }
            return output;
        }
        // ── Consumer Methods (Pull) ────────────────────────────────────────────────
        /**
         * Pulls the next available value directly from the stream.
         *
         * @returns A promise resolving to the next item `T`, or `undefined` if completed
         */
        async pull() {
            const res = await this.next();
            return res.done ? undefined : res.value;
        }
        /**
         * Advances the async iterator to the next available item.
         */
        async next() {
            if (this.#buffer.length > 0) {
                const value = this.#buffer.shift();
                return { done: false, value };
            }
            if (this.#error && !this.#catch) {
                throw this.#error;
            }
            if (this.#completed || !this.#active) {
                return { done: true, value: undefined };
            }
            const waiter = new Pledge(this.#tag ? `${this.#tag}.waiter` : 'Reactive.waiter');
            this.#waiters.push(waiter);
            return waiter.promise;
        }
        /**
         * Closes the async iterator and marks the stream as completed.
         */
        async return() {
            this.complete();
            return { done: true, value: undefined };
        }
        /**
         * Throws an error into the stream, rejecting pending waiters and stopping the stream.
         */
        async throw(err) {
            const errorObj = err instanceof Error ? err : new Error(String(err));
            this.error(errorObj);
            this.complete();
            return { done: true, value: undefined };
        }
        /** Returns this instance as an AsyncIterator */
        [Symbol.asyncIterator]() {
            return this;
        }
        // ── Resource Management ────────────────────────────────────────────────────
        /** Synchronous disposal hook (`using stream = new Reactive()`) */
        [Symbol.dispose]() {
            this.complete();
        }
        /** Asynchronous disposal hook (`await using stream = new Reactive()`) */
        async [Symbol.asyncDispose]() {
            this.complete();
        }
        // ── Static Factories ───────────────────────────────────────────────────────
        /**
         * Creates a `Reactive` stream from an iterable, async iterable, or Promise.
         *
         * @param source - The source collection or promise
         * @param options - Optional configuration options
         * @returns A Reactive stream instance
         */
        static from(source, options) {
            const stream = new Reactive(options);
            if (isDefined(source) && isFunction(source[Symbol.asyncIterator])) {
                (async () => {
                    try {
                        for await (const item of source) {
                            if (!stream.state.active)
                                break;
                            stream.push(item);
                        }
                        stream.complete();
                    }
                    catch (err) {
                        stream.error(err instanceof Error ? err : new Error(String(err)));
                        stream.complete();
                    }
                })();
            }
            else if (isDefined(source) && isFunction(source[Symbol.iterator])) {
                try {
                    for (const item of source) {
                        if (!stream.state.active)
                            break;
                        stream.push(item);
                    }
                    stream.complete();
                }
                catch (err) {
                    stream.error(err instanceof Error ? err : new Error(String(err)));
                    stream.complete();
                }
            }
            else if (isPromise(source) || (isDefined(source) && isFunction(source.then))) {
                source
                    .then((val) => {
                    stream.push(val);
                    stream.complete();
                })
                    .catch((err) => {
                    stream.error(err instanceof Error ? err : new Error(String(err)));
                    stream.complete();
                });
            }
            return stream;
        }
        static {
            __runInitializers(_classThis, _classExtraInitializers);
        }
    };
    return Reactive = _classThis;
})();
export { Reactive };
export default Reactive;
